/**
 * Report Service - Integracao com Geracao de Relatorio
 * @module features/chat/services/report.service
 *
 * Servico para integracao com o endpoint /api/report/generate.
 * Preparacao/stub para quando US-008 for implementada.
 *
 * T003-INT: Integracao com Geracao de Relatorio
 *
 * DEPENDENCIA: US-008 (Geracao de Relatorio HTML)
 * STATUS: Endpoint /api/report/generate ainda NAO existe
 *
 * Comportamento atual:
 * - Se endpoint retornar 404: loga warning e retorna mock response
 * - Se endpoint existir: processa resposta normalmente
 * - Outros erros HTTP: throw error
 */

import type { AssessmentType } from '../types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Dados do usuario para o relatorio
 */
export interface ReportUserData {
  name: string;
  email: string;
  company: string | undefined;
  role: string | undefined;
}

/**
 * Mensagem do chat para contexto do relatorio
 */
export interface ReportMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Request para geracao de relatorio
 */
export interface GenerateReportRequest {
  /** ID da sessao de assessment */
  sessionId: string;

  /** Tipo de assessment (tech ou governance) */
  assessmentType: AssessmentType;

  /** Dados do usuario */
  userData: ReportUserData;

  /** Historico de mensagens do chat */
  messages: ReportMessage[];
}

/**
 * Metadados do relatorio gerado
 */
export interface ReportMetadata {
  /** Score geral do assessment (0-100) */
  overallScore: number;

  /** Tipo de assessment */
  assessmentType: AssessmentType;

  /** Tempo de geracao em ms */
  generationTimeMs: number;

  /** Scores por dimensao (opcional) */
  dimensions?: {
    dora?: number;
    devex?: number;
    lean?: number;
    [key: string]: number | undefined;
  };
}

/**
 * Response da geracao de relatorio
 */
export interface GenerateReportResponse {
  /** Se a geracao foi bem sucedida */
  success: boolean;

  /** URL do relatorio gerado (vazio se falhou) */
  reportUrl: string;

  /** ID da sessao */
  sessionId: string;

  /** Timestamp de geracao (ISO 8601) */
  generatedAt: string;

  /** Metadados do relatorio */
  metadata: ReportMetadata;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * URL padrao da API quando VITE_API_URL nao esta definida
 */
export const DEFAULT_API_URL = 'http://localhost:3001';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Obtem a URL base da API
 * Exportada para permitir testes
 */
export function getApiUrl(): string {
  return import.meta.env.VITE_API_URL || DEFAULT_API_URL;
}

// =============================================================================
// SERVICE
// =============================================================================

/**
 * Gera relatorio de assessment
 *
 * Envia dados da sessao para o backend gerar relatorio HTML.
 * Se o endpoint nao existir (404), retorna mock response para nao quebrar o fluxo.
 *
 * @param data - Dados para geracao do relatorio
 * @param apiUrl - URL base da API (opcional, usa VITE_API_URL por padrao)
 * @returns Promise com resposta da geracao
 * @throws Error em caso de falha HTTP (exceto 404)
 *
 * @example
 * ```typescript
 * const response = await generateReport({
 *   sessionId: 'sess-123',
 *   assessmentType: 'tech',
 *   userData: { name: 'John', email: 'john@email.com' },
 *   messages: [{ role: 'user', content: 'Hello' }],
 * });
 *
 * if (response.success) {
 *   console.log('Report URL:', response.reportUrl);
 * }
 * ```
 */
export async function generateReport(
  data: GenerateReportRequest,
  apiUrl?: string
): Promise<GenerateReportResponse> {
  const baseUrl = apiUrl ?? getApiUrl();

  // Map frontend field names to backend expected format
  const backendPayload = {
    sessionId: data.sessionId,
    assessmentType: data.assessmentType,
    userData: data.userData,
    conversationHistory: data.messages, // Backend expects 'conversationHistory', not 'messages'
  };

  try {
    const response = await fetch(`${baseUrl}/api/report/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(backendPayload),
    });

    if (!response.ok) {
      // Se 404, endpoint nao existe ainda (US-008 pendente)
      if (response.status === 404) {
        console.warn('[Report] Endpoint /api/report/generate not implemented yet (US-008)');
        // Retornar mock response para nao quebrar fluxo
        return {
          success: false,
          reportUrl: '',
          sessionId: data.sessionId,
          generatedAt: new Date().toISOString(),
          metadata: {
            overallScore: 0,
            assessmentType: data.assessmentType,
            generationTimeMs: 0,
          },
        };
      }
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[Report] Generation failed:', error);
    throw error;
  }
}
